import { useState } from 'react'
import { motion } from 'framer-motion'
import { X, UserPlus, Loader2 } from 'lucide-react'
import { useAuthStore } from '@personic/shared'
import { useCreateUser } from '@/hooks/useAdminUsers'
import { useAdminTenants } from '@/hooks/useAdminTenants'

interface CreateUserModalProps {
  onClose: () => void
  onSuccess: (userId: string) => void
}

const ROLE_OPTIONS_BY_ACTOR: Record<string, { value: string; label: string }[]> = {
  superuser: [
    { value: 'admin', label: 'Admin' },
    { value: 'employee', label: 'Employee' },
    { value: 'tenant_admin', label: 'Tenant Admin' },
    { value: 'consumer', label: 'Consumer' },
  ],
  admin: [
    { value: 'employee', label: 'Employee' },
    { value: 'tenant_admin', label: 'Tenant Admin' },
    { value: 'consumer', label: 'Consumer' },
  ],
}

export default function CreateUserModal({ onClose, onSuccess }: CreateUserModalProps) {
  const { user: currentUser } = useAuthStore()
  const createUser = useCreateUser()
  const { data: tenantsData } = useAdminTenants({ limit: 200 })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    tenant_id: '',
    is_active: true,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const roleOptions = ROLE_OPTIONS_BY_ACTOR[currentUser?.role || ''] || []
  const showTenantField = formData.role === 'tenant_admin'

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!formData.name.trim()) errs.name = 'Name is required'
    if (!formData.email.trim()) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email'
    if (!formData.password) errs.password = 'Password is required'
    else if (formData.password.length < 8) errs.password = 'Min 8 characters'
    if (!formData.role) errs.role = 'Role is required'
    if (formData.role === 'tenant_admin' && !formData.tenant_id) errs.tenant_id = 'Tenant is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    try {
      const result = await createUser.mutateAsync({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role,
        tenant_id: showTenantField ? formData.tenant_id : undefined,
        is_active: formData.is_active,
      })
      onSuccess(result.id)
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (typeof detail === 'string') {
        setErrors({ submit: detail })
      } else {
        setErrors({ submit: 'Failed to create user. Please try again.' })
      }
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-midnight-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl border border-warm-200 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-copper-50 flex items-center justify-center">
              <UserPlus className="w-4.5 h-4.5 text-copper-500" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-midnight-800">Create User</h2>
              <p className="text-xs text-warm-500">Add a new user to the platform</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-warm-50 transition-colors">
            <X className="w-4 h-4 text-warm-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
              {errors.submit}
            </div>
          )}

          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(d => ({ ...d, name: e.target.value }))}
              className={`input w-full ${errors.name ? 'border-red-300 ring-red-200' : ''}`}
              placeholder="Full name"
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData(d => ({ ...d, email: e.target.value }))}
              className={`input w-full ${errors.email ? 'border-red-300 ring-red-200' : ''}`}
              placeholder="user@example.com"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="label">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={e => setFormData(d => ({ ...d, password: e.target.value }))}
              className={`input w-full ${errors.password ? 'border-red-300 ring-red-200' : ''}`}
              placeholder="Minimum 8 characters"
            />
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
          </div>

          <div>
            <label className="label">Role</label>
            <select
              value={formData.role}
              onChange={e => setFormData(d => ({ ...d, role: e.target.value, tenant_id: '' }))}
              className={`input w-full ${errors.role ? 'border-red-300 ring-red-200' : ''}`}
            >
              {roleOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.role && <p className="text-xs text-red-500 mt-1">{errors.role}</p>}
          </div>

          {showTenantField && (
            <div>
              <label className="label">Tenant</label>
              <select
                value={formData.tenant_id}
                onChange={e => setFormData(d => ({ ...d, tenant_id: e.target.value }))}
                className={`input w-full ${errors.tenant_id ? 'border-red-300 ring-red-200' : ''}`}
              >
                <option value="">Select a tenant...</option>
                {tenantsData?.tenants?.map((t: any) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                ))}
              </select>
              {errors.tenant_id && <p className="text-xs text-red-500 mt-1">{errors.tenant_id}</p>}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={e => setFormData(d => ({ ...d, is_active: e.target.checked }))}
              className="w-4 h-4 rounded border-warm-300 text-copper-500 focus:ring-copper-400"
            />
            <label htmlFor="is_active" className="text-sm text-warm-700">Account is active</label>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createUser.isPending}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              {createUser.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Create User
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
